/**
 * Pursuer lastHeard threat lock — red skull + cross (presentation only).
 * Replaces the plain yellow crosshair; prediction logic stays in rules.
 */

/** Minimal stroke/fill surface (Phaser Graphics satisfies this). */
export type ThreatLockGfx = {
  lineStyle(width: number, color: number, alpha?: number): void;
  fillStyle(color: number, alpha?: number): void;
  lineBetween(x0: number, y0: number, x1: number, y1: number): void;
  strokeCircle(x: number, y: number, radius: number): void;
  fillCircle(x: number, y: number, radius: number): void;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  strokePath(): void;
  fillPath(): void;
};

/** Threat red — matches chase/catching palette in the scene. */
export const THREAT_LOCK_COLOR = 0xef4444;
export const THREAT_LOCK_DARK = 0x7f1d1d;
export const THREAT_LOCK_BONE = 0xfecaca;

export type DrawThreatLockOpts = {
  /** Icon radius in px (default 11). */
  radius?: number;
  color?: number;
  dark?: number;
  bone?: number;
  /** Optional tether from pursuer body to lock point. */
  fromX?: number;
  fromY?: number;
};

/**
 * Draw a red skull locking onto (x, y) with a cross motif and claw brackets.
 */
export function drawThreatLockCue(
  gfx: ThreatLockGfx,
  x: number,
  y: number,
  opts: DrawThreatLockOpts = {},
): void {
  const r = opts.radius ?? 11;
  const color = opts.color ?? THREAT_LOCK_COLOR;
  const dark = opts.dark ?? THREAT_LOCK_DARK;
  const bone = opts.bone ?? THREAT_LOCK_BONE;

  if (opts.fromX != null && opts.fromY != null) {
    gfx.lineStyle(1.25, color, 0.35);
    gfx.lineBetween(opts.fromX, opts.fromY, x, y);
  }

  // Grab brackets — closing claws around the lock point
  const claw = r * 1.55;
  gfx.lineStyle(2, color, 0.75);
  // NW claw
  gfx.beginPath();
  gfx.moveTo(x - claw, y - claw * 0.35);
  gfx.lineTo(x - claw, y - claw);
  gfx.lineTo(x - claw * 0.35, y - claw);
  gfx.strokePath();
  // NE claw
  gfx.beginPath();
  gfx.moveTo(x + claw * 0.35, y - claw);
  gfx.lineTo(x + claw, y - claw);
  gfx.lineTo(x + claw, y - claw * 0.35);
  gfx.strokePath();
  // SW claw
  gfx.beginPath();
  gfx.moveTo(x - claw, y + claw * 0.35);
  gfx.lineTo(x - claw, y + claw);
  gfx.lineTo(x - claw * 0.35, y + claw);
  gfx.strokePath();
  // SE claw
  gfx.beginPath();
  gfx.moveTo(x + claw * 0.35, y + claw);
  gfx.lineTo(x + claw, y + claw);
  gfx.lineTo(x + claw, y + claw * 0.35);
  gfx.strokePath();

  // Cross motif behind / through the skull (threat lock, not yellow +)
  const arm = r * 1.15;
  gfx.lineStyle(2.25, color, 0.9);
  gfx.lineBetween(x - arm, y - arm, x + arm, y + arm);
  gfx.lineBetween(x + arm, y - arm, x - arm, y + arm);
  gfx.lineStyle(1, bone, 0.35);
  gfx.lineBetween(x - arm * 0.7, y - arm * 0.7, x + arm * 0.7, y + arm * 0.7);
  gfx.lineBetween(x + arm * 0.7, y - arm * 0.7, x - arm * 0.7, y + arm * 0.7);

  // Cranium
  gfx.fillStyle(color, 0.88);
  gfx.fillCircle(x, y - r * 0.12, r * 0.72);
  // Jaw
  gfx.fillStyle(color, 0.88);
  gfx.fillTriangle(
    x - r * 0.42,
    y + r * 0.2,
    x + r * 0.42,
    y + r * 0.2,
    x,
    y + r * 0.78,
  );
  gfx.fillStyle(color, 0.7);
  gfx.fillCircle(x, y + r * 0.28, r * 0.38);

  // Eye sockets (hollow = darker)
  const eyeY = y - r * 0.18;
  const eyeR = r * 0.18;
  gfx.fillStyle(dark, 0.95);
  gfx.fillCircle(x - r * 0.28, eyeY, eyeR);
  gfx.fillCircle(x + r * 0.28, eyeY, eyeR);
  // Cross pupils in sockets (expression)
  gfx.lineStyle(1.1, bone, 0.85);
  const pr = eyeR * 0.7;
  for (const ex of [x - r * 0.28, x + r * 0.28]) {
    gfx.lineBetween(ex - pr, eyeY, ex + pr, eyeY);
    gfx.lineBetween(ex, eyeY - pr, ex, eyeY + pr);
  }

  // Nose notch
  gfx.fillStyle(dark, 0.9);
  gfx.fillTriangle(
    x,
    y + r * 0.02,
    x - r * 0.12,
    y + r * 0.28,
    x + r * 0.12,
    y + r * 0.28,
  );

  // Jaw teeth ticks
  gfx.lineStyle(1.2, dark, 0.8);
  gfx.lineBetween(x - r * 0.18, y + r * 0.42, x - r * 0.18, y + r * 0.58);
  gfx.lineBetween(x, y + r * 0.48, x, y + r * 0.66);
  gfx.lineBetween(x + r * 0.18, y + r * 0.42, x + r * 0.18, y + r * 0.58);

  // Outer lock ring
  gfx.lineStyle(1.5, color, 0.55);
  gfx.strokeCircle(x, y, r * 1.05);
}
